/**
 * useEditingState Hook
 *
 * Manages the state for editing existing polygon regions on the floor plan.
 * Used by the map editor to track which region is being edited and handle vertex operations.
 *
 * Features:
 * - Select a region for editing
 * - Track active vertex during dragging
 * - Update vertex positions
 * - Add/delete vertices from polygon
 * - Clear editing state
 *
 * @example
 * ```tsx
 * const {
 *   editingRegionName,
 *   editingRegion,
 *   activeVertexIndex,
 *   isDraggingVertex,
 *   selectRegionForEditing,
 *   updateVertexPosition,
 *   clearEditing,
 * } = useEditingState(regions);
 *
 * // Select a region to edit
 * selectRegionForEditing('Main Wall');
 *
 * // Update a vertex position
 * const updated = updateVertexPosition(0, { x: 0.15, y: 0.15 });
 * if (updated) {
 *   // Save updated polygon
 * }
 * ```
 */

import { useState, useCallback, useMemo } from 'react';
import type { Point, WallSectionRegion } from '@flash/shared';

/**
 * Return value from useEditingState hook
 */
export interface UseEditingStateResult {
  /** Name of the region currently being edited, or null */
  editingRegionName: string | null;

  /** The full region data being edited, or null */
  editingRegion: WallSectionRegion | null;

  /** Index of the currently active vertex (for dragging), or null */
  activeVertexIndex: number | null;

  /** Whether a vertex is currently being dragged */
  isDraggingVertex: boolean;

  /** Whether a vertex can be deleted (polygon must have > 3 vertices) */
  canDeleteVertex: boolean;

  /** Select a region for editing by wall section name */
  selectRegionForEditing: (wallSectionName: string) => void;

  /** Clear the editing state */
  clearEditing: () => void;

  /** Start dragging a vertex by index */
  startDraggingVertex: (vertexIndex: number) => void;

  /** Stop dragging the current vertex */
  stopDraggingVertex: () => void;

  /** Update a vertex position, returns the updated polygon or null */
  updateVertexPosition: (vertexIndex: number, newPosition: Point) => Point[] | null;

  /** Delete a vertex by index, returns updated polygon or null if not allowed */
  deleteVertex: (vertexIndex: number) => Point[] | null;

  /** Add a vertex at the specified index, returns updated polygon or null */
  addVertex: (afterIndex: number, point: Point) => Point[] | null;
}

/** Minimum number of vertices a polygon must have */
const MIN_POLYGON_VERTICES = 3;

/**
 * Hook for managing polygon editing state
 *
 * @param regions - Array of existing wall section regions
 * @returns Editing state and control functions
 */
export const useEditingState = (regions: WallSectionRegion[]): UseEditingStateResult => {
  // Name of the region currently being edited
  const [editingRegionName, setEditingRegionName] = useState<string | null>(null);

  // Index of the active vertex (for highlighting/dragging)
  const [activeVertexIndex, setActiveVertexIndex] = useState<number | null>(null);

  // Whether currently dragging a vertex
  const [isDraggingVertex, setIsDraggingVertex] = useState(false);

  /**
   * Get the currently editing region from the regions array
   */
  const editingRegion = useMemo(() => {
    if (!editingRegionName) return null;
    return regions.find((r) => r.wallSectionName === editingRegionName) ?? null;
  }, [editingRegionName, regions]);

  /**
   * Whether a vertex can be deleted (polygon must have more than 3 vertices)
   */
  const canDeleteVertex = useMemo(() => {
    if (!editingRegion) return false;
    return editingRegion.polygon.length > MIN_POLYGON_VERTICES;
  }, [editingRegion]);

  /**
   * Select a region for editing
   */
  const selectRegionForEditing = useCallback((wallSectionName: string) => {
    setEditingRegionName(wallSectionName);
    setActiveVertexIndex(null);
    setIsDraggingVertex(false);
  }, []);

  /**
   * Clear the editing state
   */
  const clearEditing = useCallback(() => {
    setEditingRegionName(null);
    setActiveVertexIndex(null);
    setIsDraggingVertex(false);
  }, []);

  /**
   * Start dragging a vertex
   */
  const startDraggingVertex = useCallback(
    (vertexIndex: number) => {
      if (!editingRegionName) return;
      setActiveVertexIndex(vertexIndex);
      setIsDraggingVertex(true);
    },
    [editingRegionName]
  );

  /**
   * Stop dragging the current vertex
   */
  const stopDraggingVertex = useCallback(() => {
    setIsDraggingVertex(false);
  }, []);

  /**
   * Update a vertex position
   * Returns the updated polygon or null if not valid
   */
  const updateVertexPosition = useCallback(
    (vertexIndex: number, newPosition: Point): Point[] | null => {
      if (!editingRegion) return null;

      const polygon = editingRegion.polygon;
      if (vertexIndex < 0 || vertexIndex >= polygon.length) return null;

      // Create new polygon with updated vertex
      const updatedPolygon = polygon.map((point, index) =>
        index === vertexIndex ? newPosition : point
      );

      return updatedPolygon;
    },
    [editingRegion]
  );

  /**
   * Delete a vertex from the polygon
   * Returns the updated polygon or null if deletion not allowed
   */
  const deleteVertex = useCallback(
    (vertexIndex: number): Point[] | null => {
      if (!editingRegion) return null;

      const polygon = editingRegion.polygon;

      // Cannot delete if it would leave fewer than 3 vertices
      if (polygon.length <= MIN_POLYGON_VERTICES) return null;

      if (vertexIndex < 0 || vertexIndex >= polygon.length) return null;

      // Clear active vertex if it's the one being deleted
      if (activeVertexIndex === vertexIndex) {
        setActiveVertexIndex(null);
      }

      // Create new polygon without the deleted vertex
      const updatedPolygon = polygon.filter((_, index) => index !== vertexIndex);

      return updatedPolygon;
    },
    [editingRegion, activeVertexIndex]
  );

  /**
   * Add a vertex to the polygon at the specified index
   * Returns the updated polygon or null if not valid
   */
  const addVertex = useCallback(
    (afterIndex: number, point: Point): Point[] | null => {
      if (!editingRegion) return null;

      const polygon = editingRegion.polygon;

      // Insert the new point after the specified index
      const updatedPolygon = [...polygon.slice(0, afterIndex), point, ...polygon.slice(afterIndex)];

      return updatedPolygon;
    },
    [editingRegion]
  );

  return {
    editingRegionName,
    editingRegion,
    activeVertexIndex,
    isDraggingVertex,
    canDeleteVertex,
    selectRegionForEditing,
    clearEditing,
    startDraggingVertex,
    stopDraggingVertex,
    updateVertexPosition,
    deleteVertex,
    addVertex,
  };
};

export default useEditingState;
